/** English formatting locale of the first release (decision P-3). Not a user setting. */
export const FORMAT_LOCALE = "en-US";

export type FormatInputErrorCode = "not_finite" | "not_integer" | "unsafe_integer" | "negative" | "currency" | "time_zone" | "instant" | "calendar_date" | "wall_time" | "decimal" | "unit";

/**
 * Thrown for a value a formatter cannot represent truthfully (NaN, an unknown
 * currency, a naive timestamp...). Formatters never print "NaN", never fall
 * back to 0 and never guess a currency or a time zone.
 */
export class FormatInputError extends RangeError {
  readonly code: FormatInputErrorCode;

  constructor(code: FormatInputErrorCode, detail: string) {
    super(`${code}: ${detail}`);
    this.name = "FormatInputError";
    this.code = code;
  }
}

const numberFormats = new Map<string, Intl.NumberFormat>();

/** Intl.NumberFormatOptions including ES2023's signDisplay "negative", missing from ES2022 type libraries. */
export type NumberOptions = Omit<Intl.NumberFormatOptions, "signDisplay"> & { signDisplay?: "auto" | "never" | "always" | "exceptZero" | "negative" };

/**
 * Cached Intl.NumberFormat for the format locale. signDisplay "negative"
 * (no sign on an amount that rounds to zero) is dropped on engines without it.
 */
export function numberFormat(options: NumberOptions): Intl.NumberFormat {
  const key = JSON.stringify(options);
  let format = numberFormats.get(key);
  if (!format) {
    try {
      format = new Intl.NumberFormat(FORMAT_LOCALE, options as unknown as Intl.NumberFormatOptions);
    } catch (e) {
      if (!(e instanceof RangeError) || options.signDisplay !== "negative") throw e;
      format = new Intl.NumberFormat(FORMAT_LOCALE, { ...options, signDisplay: "auto" } as Intl.NumberFormatOptions);
    }
    numberFormats.set(key, format);
  }
  return format;
}

export function assertFinite(value: number, what: string): void {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new FormatInputError("not_finite", `${what} is ${String(value)}`);
}

/** -0 prints as "-0.00" in Intl; a zero amount has no sign. */
export const unsigned0 = (value: number) => (value === 0 ? 0 : value);
